import { Injectable } from '@angular/core';
import { firstValueFrom } from 'rxjs';
import { Fund } from 'src/app/domain/fund-performance/dto/Fund';
import { FundPerformanceBackendService } from 'src/app/domain/fund-performance/service/fund-performance-backend.service';
import { OrganizationsService } from 'src/app/domain/organization/components/service/organizations.service';
import { ManagementEntity } from 'src/app/domain/organization/dto/ManagementEntity';
import { ManagementEntityService } from 'src/app/domain/organization/service/management-entity.service';
import { FinancialStatementBackendService } from '../common/financial-statement-backend.service';
import { FsSelectorPeriod } from '../../utils/common/FsSelectorPeriod';
import { FinancialStatementService } from '../common/financial-statement.service';
import { PeriodService } from 'src/app/core/service/period.service';
import { DateService } from 'src/app/util/common/date-service/date.service';
import { FsGettersSettersService } from '../common/fs-getters-setters.service';
import { FsStatus, OwnershipEntityType } from '../../utils/common/utilityClasses';


@Injectable({
  providedIn: 'root',
})
export class FsNavigationService {
  /**
   *  Variable to track whether to take sessionStorage period or not - taken on refresh, processed normally if false
   */
  isRefresh = true;
  constructor(
    private orgService: OrganizationsService,
    private fsService: FinancialStatementService,
    private financialStatementBackendService: FinancialStatementBackendService,
    private managementEntityService: ManagementEntityService,
    private fpService: FundPerformanceBackendService,
    private periodService: PeriodService,
    private fsGetterSetter: FsGettersSettersService,
  ) {}

  /**
   * sets selected entity and triggers periods setup which will in turn trigger fs data
   **/
  async changeSelectedOwnershipEntity(newOwner: Fund | ManagementEntity): Promise<void> {
    if (newOwner?.uuid === this.fsGetterSetter.selectedOwnershipEntity?.value?.uuid) {
      return;
    }
    this.fsGetterSetter.setSelectedOwnershipEntity(newOwner);

    FsGettersSettersService.setSavedFsOwnershipEntityId(newOwner?.id ?? null);
    await this.periodsSetup();
  }

  async periodChange(period: FsSelectorPeriod): Promise<void> {
    if (!this.fsGetterSetter.selectedOwnershipEntity.value.uuid) {
      return;
    }
    this.fsGetterSetter.setSelectedEntityPeriod(period);
    if (period) {
      this.periodService.set(String(period.period));
    }
    await this.fsService.updateFinancialStatementData(false);
  }

  changeStatus(isCompleted: boolean): void {
    // Safely get the current financial statement
    const financialStatement = this.fsService.financialStatement;
    if (!financialStatement) {
      return;
    }
    // Update isCompleted status
    financialStatement.isCompleted = isCompleted;
    // Update the status of the selected period and set it
    const currentPeriod = this.fsGetterSetter.selectedEntityPeriod.value;
    if (currentPeriod) {
      const updatedStatus = isCompleted ? FsStatus.completed : FsStatus.started;
      const updatedPeriod = currentPeriod.changeStatusAndIconAndReturnNew(updatedStatus);
      this.fsGetterSetter.setSelectedEntityPeriod(updatedPeriod);
      const ownershipPeriods = this.fsGetterSetter.ownershipEntityPeriods.value || [];
      const periodToUpdate = ownershipPeriods.find((period) => period.period === updatedPeriod.period);
      if (periodToUpdate) {
        periodToUpdate.setStatusAndIcon(updatedPeriod.status);
      }
      this.fsGetterSetter.ownershipEntityPeriods.next(ownershipPeriods);
    }
    // Call the backend service to persist the change
    void firstValueFrom(this.financialStatementBackendService.changeFsStatus(financialStatement.uuid, isCompleted));
  }

  findPeriodsForCopy(): Date[] {
    const selectedPeriod = this.fsGetterSetter.selectedEntityPeriod.value.period;
    return this.fsGetterSetter.ownershipEntityPeriods.value
      .filter((period) =>
        (period.period !== selectedPeriod) && (period.status !== FsStatus.none))
      .map((p) => p.period);
  }

  clearData(): void {
    this.fsService.clearData();
    this.fsGetterSetter.setOwnershipEntityPeriods([]);
    this.fsGetterSetter.setSelectedEntityPeriod(null);
  }

  async periodsSetup(): Promise<void> {
    let entityPeriods: Date[];
    const selectedEntity = this.fsGetterSetter.selectedOwnershipEntity.value;
    if ('fundId' in selectedEntity) {
      // Fetch fund periods using await
      const fpDTOs = await firstValueFrom(this.fpService.getAllFundPerformancesByFundUuid(selectedEntity.uuid, true, false));
      if (!fpDTOs) {
        return;
      }
      entityPeriods = fpDTOs.filter((fp) => fp.isVisible).map((fp) => fp.period);
      this.fsService.ownershipEntityType = OwnershipEntityType.FUND;
    } else {
      // Fetch periods by management entity
      entityPeriods = await firstValueFrom(this.managementEntityService.getPeriods(selectedEntity.uuid));
      this.fsService.ownershipEntityType = OwnershipEntityType.MANAGEMENT_ENTITY;
    }
    // Resolve the data with the fetched entity periods
    await this.resolveData(entityPeriods);
  }

  async resolveData(entityPeriods: Date[]): Promise<void> {
    if (!entityPeriods?.length) {
      this.clearData();
    } else {
      await this.periodSelectorSetup(entityPeriods);
    }
  }

  async getEntities(): Promise<void> {
    const org = this.orgService.selected$.value;
    if (!org?.uuid) {
      return;
    }
    const funds = org.funds || [];
    const managementEntities = (await this.managementEntityService.get(org.uuid)) || [];
    this.fsGetterSetter.setOwnershipEntities([...funds, ...managementEntities]);
  }

  async periodSelectorSetup(entityPeriods: Date[]): Promise<void> {
    const periods: FsSelectorPeriod[] = [];
    const selectedEntity = this.fsGetterSetter.selectedOwnershipEntity.value;
    if (!selectedEntity.uuid) {
      return;
    }
    const financialStatementPeriodsStatus = await firstValueFrom(this.financialStatementBackendService.
      getAllFinancialStatementsStatusForEntity(selectedEntity.uuid, this.fsService.ownershipEntityType));
    entityPeriods.forEach((period) => {
      const periodStatus = financialStatementPeriodsStatus?.find((ps) => ps.period === period);
      if (periodStatus) {
        periods.push(new FsSelectorPeriod(period, periodStatus.isCompleted ? FsStatus.completed : FsStatus.started));
      } else {
        periods.push(new FsSelectorPeriod(period, FsStatus.none));
      }
    });
    if (periods.length) {
      this.sortPeriods(periods);
      this.fsGetterSetter.setOwnershipEntityPeriods(periods);
    }
    this.setSelectedPeriodFromLocalStorage();
  }

  async createPeriod(period: string, uuid: string): Promise<void> {
    const newPeriod = await firstValueFrom(this.managementEntityService.createPeriod(DateService.createUTCDate(period), uuid));
    const periods = this.fsGetterSetter.ownershipEntityPeriods.value.concat([new FsSelectorPeriod(newPeriod, FsStatus.none)]);
    this.sortPeriods(periods);
    this.fsGetterSetter.setOwnershipEntityPeriods(periods);
    void this.periodChange(periods.find((p) => String(p.period) === period));
  }

  async deletePeriod(periodSelector: FsSelectorPeriod, uuid: string): Promise<void> {
    this.fsService.clearData();
    await firstValueFrom(this.managementEntityService.deletePeriod(periodSelector.period, uuid));
    void this.periodsSetup();
  }

  sortPeriods(periods: FsSelectorPeriod[]): void {
    periods.sort((a, b) => new Date(b.period).getTime() - new Date(a.period).getTime());
  }

  clearAllData(): void {
    const selectedPeriod = this.fsGetterSetter.selectedEntityPeriod.value;
    this.fsGetterSetter.setSelectedEntityPeriod(selectedPeriod?.changeStatusAndIconAndReturnNew(FsStatus.none));
    const updatedOwnershipEntityPeriods = this.fsGetterSetter.ownershipEntityPeriods.value
      .map((period) =>
        (period.period === selectedPeriod.period
          ? period.changeStatusAndIconAndReturnNew(FsStatus.none)
          : period),
      );
    this.fsGetterSetter.setOwnershipEntityPeriods(updatedOwnershipEntityPeriods);
    void this.fsService.deleteFsData();
  }

  checkCapAccountManagement(): void {
    const fs = this.fsService.financialStatement;
    if (!fs || !fs.balanceSheetParts.length) {
      return;
    }
    this.fsGetterSetter.setIsCapAccountManagementLocked(!fs.balanceSheetParts[2].sums.yearlySums.size
        && !fs.profitLossParts[0].sums.yearlySums.size
        && !fs.profitLossParts[1].sums.yearlySums.size);
  }

  private setSelectedPeriodFromLocalStorage(): void {
    const selectedPeriodFromStorage = this.periodService.get();
    const ownershipEntityPeriods = this.fsGetterSetter.ownershipEntityPeriods.value;
    if (!ownershipEntityPeriods || ownershipEntityPeriods.length === 0) {
      return;
    }
    const selectedPeriod = ownershipEntityPeriods.find(
      (period) => period.period.toString() === selectedPeriodFromStorage
    );
    if (selectedPeriod && this.isRefresh) {
      void this.periodChange(selectedPeriod);
      this.isRefresh = false;
      return;
    }
    if (selectedPeriod && selectedPeriod.status !== FsStatus.none) {
      void this.periodChange(selectedPeriod);
      return;
    }
    const periodsWithFs = ownershipEntityPeriods.filter(
      (periodWithFs) => periodWithFs.status !== FsStatus.none
    );
    const periodToSelect = periodsWithFs.length > 0
      ? periodsWithFs[0]
      : ownershipEntityPeriods[0];
    void this.periodChange(periodToSelect);
  }
}
